"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.parseArguments = parseArguments;
exports["default"] = void 0;

var _isomorphicFetch = _interopRequireDefault(require("isomorphic-fetch"));

var _utils = require("../utils");

var _httpError = _interopRequireDefault(require("../http-error"));

var _composeMiddleware = _interopRequireDefault(require("./compose-middleware"));

var _middleware = require("./middleware");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 *
 * A wrapper function for the [Fetch API](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API)
 * that adds default request settings and handles CRSF token logic.
 *
 * This function adds the following config settings to the given request:
 * ```
 * {
 *   credentials: 'same-origin',
 *   headers: [
 *     'Accept': 'application/json',
 *     'X-Requested-With': 'XMLHttpRequest',
 *     'Content-Type': 'application/json'
 *   ],
 *   mode: 'same-origin'
 * }
 * ```
 * Any one of these settings can be overriden using the passed-in config object.
 *
 * In addition to the normal Fetch API settings, the config object may also contain these special settings just for `http`:
 * - `'url'`: The url for the request. This can also be passed in directly as the first argument (see shorthand example).
 * - `'root'`: A path to be appended to the given url (default=`''`).
 * - `'crsf'`: The name of the `meta` tag containing the CSRF token (default=`'csrf-token'`). This can be set to `false` to prevent a token from being sent.
 * - `'before'`: A function that's called before the request executes. This function is passed the request options and its return value will be added to those options.
 *    It can also return a promise that resolves to a new options object.
 * - `'bearerToken'`: A token to use for bearer auth. If provided, `http` will add the header `"Authorization": "Bearer <bearerToken>"` to the request.
 * - `'onSuccess'`: A function that will be called if the request succeeds. It will be passed the successful response. If it returns a value, `http` will resolve with this value instead of the response.
 * - `'onFailure'`: A function that will be called if the request fails. It will be passed the error that was thrown during the request. If it returns a value, `http` will reject with this value instead of the default error.
 * - `successDataPath`: A path to response data that the promise will resolve with.
 * - `failureDataPath`: A path to the errors that will be included in the HttpError object (default=`'errors'`)
 * - `query`: An object that will be transformed into a query string and appended to the request URL.
 * - `overrideHeaders`: A boolean flag indicating whether or not default headers should be included in the request (default=`false`).
 * - `camelizeResponse`: A boolean flag indicating whether or not to camelize the response keys (default=`true`). The helper function that does this is also exported from this library as `camelizeKeys`.
 * - `decamelizeBody`: A boolean flag indicating whether or not to decamelize the body keys (default=`true`). The helper function that does this is also exported from this library as `decamelizeKeys`.
 * - `decamelizeQuery`: A boolean flag indicating whether or not to decamelize the query string keys (default=`true`).
 * - `parseJsonStrictly`: A boolean flag indicating whether or not to return the text of the response body if JSON parsing fails (default=`true`). If set to `true` and invalid JSON is received in the response, then `null` will be returned instead.
 * - `auth`: An object with the following keys `{ username, password }`. If present, `http` will use [basic auth](https://en.wikipedia.org/wiki/Basic_access_authentication#Client_side), adding the header `"Authorization": "Basic <authToken>"` to the request, where `<authToken>` is a base64 encoded string of `username:password`.
 *
 * @name http
 * @type Function
 * @param {Object} config - An object containing config information for the `Fetch` request, as well as the extra keys noted above.
 * @returns {Promise} A Promise that either resolves with the response or rejects with an {@link HttpError}.
 *
 * @example
 *
 * function getUsers () {
 *   return http({
 *      url: '/users',
 *      root: 'www.my.cool.api.com',
 *      crsf: 'custom-token-name'
 *   })
 * }
 *
 * getUsers()
 *    .then(res => console.log('The users are', res))
 *    .catch(err => console.log('An error occurred!', err))
 *
 * // Shorthand: pass `url` as first argument:
 * function getUsers () {
 *   return http('/users', options)
 * }
 *
 */
// Enable shorthand with optional string `url` first argument.
function parseArguments() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  var firstArg = args[0],
      secondArg = args[1];
  var hasUrlArgument = (0, _utils.isString)(firstArg);
  if (!hasUrlArgument) return firstArg || {};
  var options = secondArg || {};
  return _objectSpread({}, options, {
    url: firstArg
  });
} // Get JSON from response


function getResponseBody(_x, _x2) {
  return _getResponseBody.apply(this, arguments);
}

function _getResponseBody() {
  _getResponseBody = _asyncToGenerator(
  /*#__PURE__*/
  regeneratorRuntime.mark(function _callee(response, _ref) {
    var parseJsonStrictly, data;
    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            parseJsonStrictly = _ref.parseJsonStrictly;

            if (!(response.headers.get('Content-Length') === '0' || response.status === 204)) {
              _context.next = 3;
              break;
            }

            return _context.abrupt("return", null);

          case 3:
            _context.prev = 3;
            _context.next = 6;
            return response.text();

          case 6:
            data = _context.sent;
            return _context.abrupt("return", JSON.parse(data));

          case 10:
            _context.prev = 10;
            _context.t0 = _context["catch"](3);

            if (!parseJsonStrictly) {
              _context.next = 15;
              break;
            }

            // eslint-disable-next-line
            console.warn('Failed to parse response body: ' + _context.t0, response);
            return _context.abrupt("return", null);

          case 15:
            return _context.abrupt("return", data);

          case 16:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[3, 10]]);
  }));
  return _getResponseBody.apply(this, arguments);
}

function http() {
  return _http.apply(this, arguments);
}

function _http() {
  _http = _asyncToGenerator(
  /*#__PURE__*/
  regeneratorRuntime.mark(function _callee3() {
    var _parseArguments,
        _parseArguments$befor,
        before,
        __mock_response,
        options,
        parsedOptions,
        onSuccess,
        onFailure,
        camelizeResponse,
        successDataPath,
        failureDataPath,
        url,
        fetchOptions,
        parseJsonStrictly,
        responseData,
        _args3 = arguments;

    return regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _parseArguments = parseArguments.apply(void 0, _args3), _parseArguments$befor = _parseArguments.before, before = _parseArguments$befor === void 0 ? _utils.noop : _parseArguments$befor, __mock_response = _parseArguments.__mock_response, options = _objectWithoutProperties(_parseArguments, ["before", "__mock_response"]);
            _context3.next = 3;
            return (0, _composeMiddleware["default"])(before, _middleware.setDefaults, _middleware.setAuthHeaders, _middleware.addRootToUrl, _middleware.addQueryToUrl, _middleware.serializeBody, _middleware.includeCSRFToken, _middleware.extractFetchOptions)(options);

          case 3:
            parsedOptions = _context3.sent;
            onSuccess = parsedOptions.onSuccess, onFailure = parsedOptions.onFailure, camelizeResponse = parsedOptions.camelizeResponse, successDataPath = parsedOptions.successDataPath, failureDataPath = parsedOptions.failureDataPath, url = parsedOptions.url, fetchOptions = parsedOptions.fetchOptions, parseJsonStrictly = parsedOptions.parseJsonStrictly; // responseData is either the body of the response, or an error object.

            _context3.next = 7;
            return (0, _utils.attemptAsync)(
            /*#__PURE__*/
            _asyncToGenerator(
            /*#__PURE__*/
            regeneratorRuntime.mark(function _callee2() {
              var _mock_response;

              var response, body, data, errors;
              return regeneratorRuntime.wrap(function _callee2$(_context2) {
                while (1) {
                  switch (_context2.prev = _context2.next) {
                    case 0:
                      if (!((_mock_response = __mock_response) !== null && _mock_response !== void 0)) {
                        _context2.next = 4;
                        break;
                      }

                      _context2.t0 = _mock_response;
                      _context2.next = 7;
                      break;

                    case 4:
                      _context2.next = 6;
                      return (0, _isomorphicFetch["default"])(url, fetchOptions);

                    case 6:
                      _context2.t0 = _context2.sent;

                    case 7:
                      response = _context2.t0;
                      _context2.next = 10;
                      return getResponseBody(response, {
                        parseJsonStrictly: parseJsonStrictly
                      });

                    case 10:
                      body = _context2.sent;
                      data = camelizeResponse ? (0, _utils.camelizeKeys)(body) : body;

                      if (response.ok) {
                        _context2.next = 15;
                        break;
                      }

                      errors = (0, _utils.getDataAtPath)(data, failureDataPath);
                      throw new _httpError["default"](response.status, response.statusText, data, errors);

                    case 15:
                      return _context2.abrupt("return", data);

                    case 16:
                    case "end":
                      return _context2.stop();
                  }
                }
              }, _callee2);
            })));

          case 7:
            responseData = _context3.sent;

            if (!(0, _utils.isError)(responseData)) {
              _context3.next = 10;
              break;
            }

            throw onFailure(responseData);

          case 10:
            return _context3.abrupt("return", onSuccess((0, _utils.getDataAtPath)(responseData, successDataPath)));

          case 11:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));
  return _http.apply(this, arguments);
}

var _default = http;
exports["default"] = _default;